import { useRef, useState } from 'react';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Typography from '@material-ui/core/Typography';
import ErrorIcon from '@material-ui/icons/Error';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import InfoIcon from '@material-ui/icons/Info';
import LightbulbIcon from '@material-ui/icons/EmojiObjects';
import WarningIcon from '@material-ui/icons/Warning';
import { useKnowledgeBuilderColors } from '../../../theme/knowledgeBuilderColors';
import { useKnowledgeBuilder } from '../KnowledgeBuilderProvider';
import type { DefinitionFinding } from '../../../api';
import { useDefinitionInterview } from './DefinitionContext';
import { buildPendingPreviewDocumentForPath } from '../proposedChangePreview';
import { MarkdownPreview } from '../MarkdownPreview';
import {
  sectionDocumentPaths,
  sectionPendingOperations,
  approveSectionOperations,
} from './sectionDocuments';

export const StructuredReviewPane = ({
  onOpenSource,
}: {
  onOpenSource(path?: string): void;
}) => {
  const [acceptError, setAcceptError] = useState<string>();
  const [accepting, setAccepting] = useState(false);
  const acceptanceLock = useRef(false);
  const colors = useKnowledgeBuilderColors();
  const { approveProposedChange, documents, proposedChanges } =
    useKnowledgeBuilder();
  const {
    schema,
    findings,
    isReviewing,
    reviewMessage,
    runReview,
    fieldFor,
    setFieldStatus,
    selectSection,
  } = useDefinitionInterview();

  const reviewLabel = reviewMessage ? 'Run AI review again' : 'Run AI review';
  const unresolvedFindings = findings.filter(finding => !finding.resolved);

  return (
    <div
      className="knowledge-builder-scrollbar"
      style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 16 }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <Typography
          style={{ fontSize: 16, fontWeight: 600, color: colors.text.primary }}
        >
          Definition Review
        </Typography>
        <Button
          size="small"
          variant="outlined"
          onClick={() => void runReview()}
          disabled={isReviewing || accepting}
          startIcon={isReviewing ? <CircularProgress size={12} /> : undefined}
        >
          {isReviewing ? 'Reviewing...' : reviewLabel}
        </Button>
      </div>

      {reviewMessage && !isReviewing && (
        <div
          style={{
            border: `1px solid ${colors.border.default}`,
            borderRadius: 8,
            padding: '10px 12px',
            marginBottom: 16,
            background: colors.bg.panelAlt,
          }}
        >
          <Typography
            style={{
              fontSize: 11,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              color: colors.text.muted,
              marginBottom: 6,
            }}
          >
            AI review summary
          </Typography>
          <Typography
            style={{
              fontSize: 13,
              color: colors.text.primary,
              whiteSpace: 'pre-wrap',
            }}
          >
            {reviewMessage}
          </Typography>
        </div>
      )}

      {acceptError && (
        <Typography role="alert" color="error">
          {acceptError}
        </Typography>
      )}
      {(schema?.sections ?? []).map(section => {
        const paths = sectionDocumentPaths(section, documents);
        const pendingOperations = sectionPendingOperations(
          proposedChanges,
          paths,
        );
        const previews = paths.map(path => ({
          path,
          document:
            buildPendingPreviewDocumentForPath({
              path,
              documents,
              proposedChanges,
            }) ?? documents.find(item => item.path === path),
        }));
        const missingDocuments = previews.some(
          item => !item.document || !item.document.content.trim(),
        );
        const unansweredRequired = section.fields.some(field => {
          const state = fieldFor(section.id, field.id);
          return (
            field.required &&
            (!state?.value?.trim() ||
              state.status === 'deferred' ||
              state.status === 'unconfirmed')
          );
        });
        const acceptLabel = pendingOperations.length
          ? `Accept changes (${pendingOperations.length})`
          : 'Accept';
        const accepted = sectionAccepted(section, fieldFor);
        const unresolved = sectionUnresolved(section, fieldFor);
        const hasAcceptableChanges = pendingOperations.length > 0 || !accepted;
        const sectionFindings = unresolvedFindings.filter(
          finding => finding.sectionId === section.id,
        );

        return (
          <div
            key={section.id}
            style={{
              border: `1px solid ${colors.border.default}`,
              borderRadius: 8,
              marginBottom: 14,
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: 8,
                right: 10,
                display: 'flex',
                gap: 4,
                zIndex: 1,
              }}
            >
              {accepted && (
                <CheckCircleIcon
                  titleAccess="Accepted"
                  style={{ fontSize: 16, color: colors.accent.blue }}
                />
              )}
              {unresolved && (
                <ErrorOutlineIcon
                  titleAccess="Marked unresolved"
                  style={{ fontSize: 16, color: colors.status.warningText }}
                />
              )}
            </div>
            <div
              style={{
                padding: '8px 44px 8px 12px',
                borderBottom: `1px solid ${colors.border.default}`,
                background: colors.bg.panelAlt,
              }}
            >
              <Typography
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: colors.text.primary,
                }}
              >
                {section.label}
              </Typography>
            </div>
            <div style={{ padding: '10px 12px' }}>
              {previews.length === 0 && (
                <Typography>Not drafted yet.</Typography>
              )}
              {previews.map(({ path, document }) => (
                <details key={path} open={previews.length === 1}>
                  <summary>{path}</summary>
                  {document?.content.trim() ? (
                    <MarkdownPreview document={document} />
                  ) : (
                    <Typography role="alert" color="error">
                      Missing or empty document: {path}
                    </Typography>
                  )}
                  <Button size="small" onClick={() => onOpenSource(path)}>
                    Open source
                  </Button>
                </details>
              ))}
              {unansweredRequired && (
                <Typography color="error">
                  Required answers are still unresolved. Continue the interview
                  before accepting this section.
                </Typography>
              )}
              {sectionFindings.map(finding => (
                <FindingRow key={finding.id} finding={finding} />
              ))}
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 6,
                  marginTop: 10,
                }}
              >
                <Button
                  size="small"
                  disabled={accepting}
                  onClick={() => selectSection(section.id)}
                >
                  Change with AI
                </Button>
                <Button
                  size="small"
                  disabled={accepting}
                  onClick={() =>
                    reopenSection(section, fieldFor, setFieldStatus).then(() =>
                      selectSection(section.id),
                    )
                  }
                >
                  Reopen question
                </Button>
                <Button
                  size="small"
                  disabled={
                    !hasAcceptableChanges ||
                    accepting ||
                    missingDocuments ||
                    unansweredRequired
                  }
                  onClick={async () => {
                    if (acceptanceLock.current) return;
                    acceptanceLock.current = true;
                    setAccepting(true);
                    setAcceptError(undefined);
                    try {
                      await approveSectionOperations(
                        proposedChanges,
                        paths,
                        approveProposedChange,
                      );
                      await acceptSection(section, fieldFor, setFieldStatus);
                    } catch (error) {
                      setAcceptError(
                        error instanceof Error
                          ? error.message
                          : 'Unable to accept section',
                      );
                    } finally {
                      acceptanceLock.current = false;
                      setAccepting(false);
                    }
                  }}
                >
                  {acceptLabel}
                </Button>
                <Button
                  size="small"
                  disabled={accepted || accepting}
                  onClick={() =>
                    deferSection(section, fieldFor, setFieldStatus)
                  }
                >
                  Mark unresolved
                </Button>
              </div>
            </div>
          </div>
        );
      })}

      <Typography
        style={{
          fontSize: 13,
          fontWeight: 600,
          marginTop: 8,
          marginBottom: 8,
          color: colors.text.primary,
        }}
      >
        Review findings ({unresolvedFindings.length})
      </Typography>
      {unresolvedFindings.length === 0 && (
        <Typography style={{ fontSize: 12, color: colors.text.muted }}>
          No open findings.
        </Typography>
      )}
    </div>
  );
};

function FindingRow({ finding }: { finding: DefinitionFinding }) {
  const colors = useKnowledgeBuilderColors();
  const icons = {
    ERROR: (
      <ErrorIcon style={{ fontSize: 14, color: colors.status.errorText }} />
    ),
    WARNING: (
      <WarningIcon style={{ fontSize: 14, color: colors.status.warningText }} />
    ),
    SUGGESTION: (
      <LightbulbIcon style={{ fontSize: 14, color: colors.accent.blue }} />
    ),
    INFORMATION: (
      <InfoIcon style={{ fontSize: 14, color: colors.text.muted }} />
    ),
  };
  const icon = icons[finding.severity];
  return (
    <div
      style={{
        display: 'flex',
        gap: 6,
        alignItems: 'flex-start',
        marginTop: 8,
      }}
    >
      {icon}
      <Typography style={{ fontSize: 12, color: colors.text.secondary }}>
        {finding.message}
      </Typography>
    </div>
  );
}

function sectionAccepted(
  section: { id: string; fields: { id: string; required: boolean }[] },
  fieldFor: ReturnType<typeof useDefinitionInterview>['fieldFor'],
) {
  return section.fields.every(
    field => fieldFor(section.id, field.id)?.status === 'confirmed',
  );
}

function sectionUnresolved(
  section: { id: string; fields: { id: string; required: boolean }[] },
  fieldFor: ReturnType<typeof useDefinitionInterview>['fieldFor'],
) {
  return section.fields.some(
    field => fieldFor(section.id, field.id)?.status === 'deferred',
  );
}

async function acceptSection(
  section: { id: string; fields: { id: string; required: boolean }[] },
  fieldFor: ReturnType<typeof useDefinitionInterview>['fieldFor'],
  setFieldStatus: ReturnType<typeof useDefinitionInterview>['setFieldStatus'],
) {
  await Promise.all(
    section.fields.map(field => {
      const current = fieldFor(section.id, field.id);
      if (
        !current ||
        current.status === 'confirmed' ||
        current.status === 'deferred' ||
        !current.value?.trim()
      ) {
        return Promise.resolve();
      }
      return setFieldStatus(section.id, field.id, 'confirmed', current.value);
    }),
  );
}

async function reopenSection(
  section: { id: string; fields: { id: string }[] },
  fieldFor: ReturnType<typeof useDefinitionInterview>['fieldFor'],
  setFieldStatus: ReturnType<typeof useDefinitionInterview>['setFieldStatus'],
) {
  await Promise.all(
    section.fields.map(field => {
      const current = fieldFor(section.id, field.id);
      return setFieldStatus(
        section.id,
        field.id,
        'unconfirmed',
        current?.value,
      );
    }),
  );
}

async function deferSection(
  section: { id: string; fields: { id: string; required: boolean }[] },
  fieldFor: ReturnType<typeof useDefinitionInterview>['fieldFor'],
  setFieldStatus: ReturnType<typeof useDefinitionInterview>['setFieldStatus'],
) {
  await Promise.all(
    section.fields
      .filter(field => fieldFor(section.id, field.id)?.status !== 'confirmed')
      .map(field => setFieldStatus(section.id, field.id, 'deferred')),
  );
}
